import React, { FC, useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IFinance } from "../../../helpers/types";

import "./FinanceList.scss";
import FinanceForm from "../../organisms/FinanceForm/FinanceForm";
import RectButton from "../../atoms/RectButton/RectButton";
import Pagination from "../../atoms/Pagination/Pagination";
import { getTimeDate, getTransactionStatus } from "../../../helpers/functions";
import Tag from "../../atoms/Tag/Tag";

import Select from "../../atoms/Select/Select";
import DateInputGradient from "../../atoms/DateInputGradient/DateInputGradient";
import { cities, currencies } from "../../../helpers/constants";
import Input from "../Input/Input";

export type TransactionType = "TRANSFER" | "SALARY" | "EXPENSES";

export interface FinanceFilters {
    transaction_type: TransactionType;
    name: string;
    city: string;
    purchase_currency: string;
    author: string;
    dateFrom: string;
    dateTo: string;
}

interface FinanceListProps {
    items: IFinance[];
    totalCount: number;
    currentPage: number;
    pageUrl: string;
    onChangePagination: (page: number) => void;
    onDelete: (id: number) => void | Promise<void>;
    onCreate: (data: Partial<IFinance>) => void | Promise<void>;
    onUpdate: (id: number, data: Partial<IFinance>) => void | Promise<void>;
    filters: FinanceFilters;
    onChangeFilters: (filters: FinanceFilters) => void;
}

const ITEMS_PER_PAGE = 30;
const DEBOUNCE_DELAY = 500;

const formatWithCommas = (value: unknown): string => {
    if (value === null || value === undefined || value === "") return "";
    const normalized = String(value).replace(/,/g, "").trim();
    if (!/^-?\d+(\.\d+)?$/.test(normalized)) return String(value);

    const [intPartRaw, decPart] = normalized.split(".");
    const sign = intPartRaw.startsWith("-") ? "-" : "";
    const intPart = sign ? intPartRaw.slice(1) : intPartRaw;

    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return sign + formattedInt + (decPart !== undefined ? "." + decPart : "");
};

const FinanceList: FC<FinanceListProps> = ({
                                               items,
                                               totalCount,
                                               currentPage,
                                               pageUrl,
                                               onChangePagination,
                                               onDelete,
                                               onCreate,
                                               onUpdate,
                                               filters,
                                               onChangeFilters
                                           }) => {
    const [openedId, setOpenedId] = useState<number | null>(null);
    const [localFilters, setLocalFilters] = useState<FinanceFilters>(filters);
    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    const resetPageUrl = () => {
        const { pathname, search } = location;

        const normalizedPath = pathname.replace(/\/$/, "");
        const hasPage = /\/page\/\d+$/.test(normalizedPath);

        const nextPath = hasPage
            ? normalizedPath.replace(/\/page\/\d+$/, "/page/1")
            : `${normalizedPath}/page/1`;

        navigate(nextPath + search);
    };

    const applyFilters = (nextFilters: FinanceFilters, options?: { immediate?: boolean }) => {
        const immediate = options?.immediate;

        setLocalFilters(nextFilters);

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        if (immediate) {
            onChangeFilters(nextFilters);
            return;
        }

        debounceTimeoutRef.current = setTimeout(() => {
            onChangeFilters(nextFilters);
        }, DEBOUNCE_DELAY);
    };

    const toggleAccordion = (id: number) => {
        setOpenedId(prev => (prev === id ? null : id));
    };

    const handleTypeClick = (type: TransactionType) => {
        if (type === localFilters.transaction_type) return;

        onChangePagination(1);
        resetPageUrl();
        setOpenedId(null);

        const nextFilters: FinanceFilters = {
            transaction_type: type,
            name: "",
            city: "",
            purchase_currency: "",
            author: "",
            dateFrom: "",
            dateTo: ""
        };

        applyFilters(nextFilters, { immediate: true });
    };

    const typeModifier = localFilters.transaction_type.toLowerCase();

    return (
        <div className="finance">
            <div className="finance__create">
                <h3 className="finance__title">Добавить новую транзакцию</h3>
                <div className="finance__filters-types">
                    <RectButton
                        type="button"
                        name="Переводы"
                        onClick={() => handleTypeClick("TRANSFER")}
                        className={
                            localFilters.transaction_type === "TRANSFER"
                                ? "finance__filters-type finance__filters-type--active"
                                : "finance__filters-type"
                        }
                    />
                    <RectButton
                        type="button"
                        name="Зарплата"
                        onClick={() => handleTypeClick("SALARY")}
                        className={
                            localFilters.transaction_type === "SALARY"
                                ? "finance__filters-type finance__filters-type--active"
                                : "finance__filters-type"
                        }
                    />
                    <RectButton
                        type="button"
                        name="Расходы"
                        onClick={() => handleTypeClick("EXPENSES")}
                        className={
                            localFilters.transaction_type === "EXPENSES"
                                ? "finance__filters-type finance__filters-type--active"
                                : "finance__filters-type"
                        }
                    />
                </div>

                <FinanceForm
                    submitLabel="Create"
                    onSubmit={onCreate}
                    initialValues={{ transaction_type: localFilters.transaction_type } as Partial<IFinance>}
                />
            </div>

            <div className="finance__filters">
                <div className="finance__filters-row">
                    <div className="finance__filters-field">
                        <Input
                            name="nameFilter"
                            placeholder="Название / Имя"
                            value={localFilters.name}
                            onChange={e =>
                                applyFilters({
                                    ...localFilters,
                                    name: e.target.value
                                })
                            }
                        />
                    </div>

                    {(localFilters.transaction_type === "TRANSFER" ||
                        localFilters.transaction_type === "EXPENSES") && (
                        <div className="finance__filters-field finance__filters-field--select">
                            <Select
                                name="cityFilter"
                                placeholder="Город"
                                items={cities.map(c => ({ value: c, name: c }))}
                                defaultValue={localFilters.city}
                                onChange={value =>
                                    applyFilters({
                                        ...localFilters,
                                        city: value
                                    })
                                }
                                type="search"
                            />
                        </div>
                    )}

                    {localFilters.transaction_type === "TRANSFER" && (
                        <div className="finance__filters-field finance__filters-field--select">
                            <Select
                                name="currencyFilter"
                                placeholder="Валюта"
                                items={currencies}
                                defaultValue={localFilters.purchase_currency}
                                onChange={value =>
                                    applyFilters({
                                        ...localFilters,
                                        purchase_currency: value
                                    })
                                }
                            />
                        </div>
                    )}

{/*                    <div className="finance__filters-field">
                        <Input
                            name="authorFilter"
                            placeholder="Автор"
                            value={localFilters.author}
                            onChange={e =>
                                applyFilters({
                                    ...localFilters,
                                    author: e.target.value
                                })
                            }
                        />
                    </div>*/}

                    <div className="finance__filters-field">
                        <DateInputGradient
                            name="dateFrom"
                            placeholder="Дата с"
                            value={localFilters.dateFrom}
                            onChange={value =>
                                applyFilters({
                                    ...localFilters,
                                    dateFrom: value
                                })
                            }
                        />
                    </div>

                    <div className="finance__filters-field">
                        <DateInputGradient
                            name="dateTo"
                            placeholder="Дата по"
                            value={localFilters.dateTo}
                            onChange={value =>
                                applyFilters({
                                    ...localFilters,
                                    dateTo: value
                                })
                            }
                        />
                    </div>

                    <div className="finance__filters-field finance__filters-field--actions">
                        <RectButton
                            type="button"
                            name="Сбросить фильтры"
                            onClick={() => {
                                onChangePagination(1);
                                resetPageUrl();
                                applyFilters(
                                    {
                                        transaction_type: localFilters.transaction_type,
                                        name: "",
                                        city: "",
                                        purchase_currency: "",
                                        author: "",
                                        dateFrom: "",
                                        dateTo: ""
                                    },
                                    { immediate: true }
                                );
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="finance-table">
                <div className={"finance-table__header " + `finance-table__header--${typeModifier}`}>
                    <div className="finance-table__cell finance-table__cell--id">ID</div>

                    {localFilters.transaction_type === "TRANSFER" && (
                        <>
                            <div className="finance-table__cell">Название</div>
                            <div className="finance-table__cell">Сумма (RUB)</div>
                            <div className="finance-table__cell">Сумма отправки</div>
                            <div className="finance-table__cell">Сумма (перевода)</div>
                            <div className="finance-table__cell">Сумма (Закупки)</div>
                            {/*            <div className="finance-table__cell">Валюта</div>*/}
                            <div className="finance-table__cell">Прибыль USDT</div>
                            <div className="finance-table__cell">Описание</div>
                            <div className="finance-table__cell">Город</div>
                        </>
                    )}

                    {localFilters.transaction_type === "SALARY" && (
                        <>
                            <div className="finance-table__cell">Имя</div>
                            <div className="finance-table__cell">Сумма (USDT)</div>
                            <div className="finance-table__cell">Тип</div>
                            <div className="finance-table__cell">Описание</div>
                        </>
                    )}

                    {localFilters.transaction_type === "EXPENSES" && (
                        <>
                            <div className="finance-table__cell">Название</div>
                            <div className="finance-table__cell">Сумма (USDT)</div>
                            <div className="finance-table__cell">Тип</div>
                            <div className="finance-table__cell">Город</div>
                            <div className="finance-table__cell">Описание</div>
                        </>
                    )}

                    <div className="finance-table__cell finance-table__cell--date">Дата</div>
                    <div className="finance-table__cell finance-table__cell--actions"></div>
                </div>

                <div className="finance-table__body">
                    {items && items.length > 0 ? (
                        items.map((item: any) => {
                            const rowTypeModifier = item.transaction_type.toLowerCase();
                            const purchaseAmountCurrency = item.purchase_amount_currency;

                            const purchaseAmountLabel =
                                item.purchase_amount !== undefined && item.purchase_amount !== null
                                    ? `${formatWithCommas(item.purchase_amount)}${
                                        purchaseAmountCurrency ? ` ${purchaseAmountCurrency}` : ""
                                    }`
                                    : "";

                            return (
                                <div className="finance-table__item" key={item.id}>
                                    <div className={"finance-table__row " + `finance-table__row--${rowTypeModifier}`}>
                                        <div className="finance-table__cell finance-table__cell--id" data-label="ID">
                                            {item.id}
                                        </div>

                                        {localFilters.transaction_type === "TRANSFER" && (
                                            <>
                                                <div className="finance-table__cell" data-label="Название">
                                                    {item.name}
                                                </div>

                                                <div className="finance-table__cell" data-label="Сумма (RUB)">
                                                    <Tag
                                                        name={item.rub_amount ? `${formatWithCommas(item.rub_amount)} ₽` : ""}
                                                        color="#03a004"
                                                        size={"lg"}
                                                    />
                                                </div>

                                                <div className="finance-table__cell" data-label="Сумма отправки">
                                                    <Tag
                                                        name={item.send_amount ? `${formatWithCommas(item.send_amount)} USDT` : ""}
                                                        color="#4dabf7"
                                                        size={"lg"}
                                                    />
                                                </div>

                                                <div className="finance-table__cell" data-label="Сумма (перевода)">
                                                    <Tag
                                                        name={
                                                            item.transfer_amount
                                                                ? `${formatWithCommas(item.transfer_amount)} ${item.purchase_currency}`
                                                                : ""
                                                        }
                                                        color="#d17600"
                                                        size={"lg"}
                                                    />
                                                </div>

                                                <div className="finance-table__cell" data-label="Сумма (Закупки)">
                                                    <Tag name={purchaseAmountLabel} color="#9b59b6" size={"lg"} />
                                                </div>

                                                {/*                                                <div className="finance-table__cell" data-label="Валюта">
                                                    <Tag
                                                        name={item.purchase_currency ? `${item.purchase_currency}` : ""}
                                                        color={getRandomColor(item.purchase_currency)}
                                                        size={"lg"}
                                                    />
                                                </div>*/}

                                                <div className="finance-table__cell" data-label="Прибыль USDT">
                                                    <Tag
                                                        name={item.profit_rub ? `${formatWithCommas(item.profit_rub)} USDT` : ""}
                                                        color="#ff7c7c"
                                                        size={"lg"}
                                                    />
                                                </div>

                                                <div className="finance-table__cell" data-label="Описание">
                                                    {item.description}
                                                </div>

                                                <div className="finance-table__cell" data-label="Город">
                                                    {item.city}
                                                </div>
                                            </>
                                        )}

                                        {localFilters.transaction_type === "SALARY" && (
                                            <>
                                                <div className="finance-table__cell" data-label="Имя">
                                                    {item.name}
                                                </div>

                                                <div className="finance-table__cell" data-label="Сумма (USDT)">
                                                    <Tag
                                                        name={item.rub_amount ? `${formatWithCommas(item.rub_amount)} USDT` : ""}
                                                        color="#ff7c7c"
                                                        size={"lg"}
                                                    />
                                                </div>

                                                <div className="finance-table__cell" data-label="Тип">
                                                    <Tag
                                                        size={"lg"}
                                                        name={item.transaction_type ? getTransactionStatus(item.transaction_type).name : ""}
                                                        color={getTransactionStatus(item.transaction_type).textColor}
                                                    />
                                                </div>

                                                <div className="finance-table__cell" data-label="Описание">
                                                    {item.description}
                                                </div>
                                            </>
                                        )}

                                        {localFilters.transaction_type === "EXPENSES" && (
                                            <>
                                                <div className="finance-table__cell" data-label="Название">
                                                    {item.name}
                                                </div>

                                                <div className="finance-table__cell" data-label="Сумма (USDT)">
                                                    <Tag
                                                        name={item.rub_amount ? `${formatWithCommas(item.rub_amount)} USDT` : ""}
                                                        color="#ff7c7c"
                                                        size={"lg"}
                                                    />
                                                </div>

                                                <div className="finance-table__cell" data-label="Тип">
                                                    <Tag
                                                        size={"lg"}
                                                        name={item.transaction_type ? getTransactionStatus(item.transaction_type).name : ""}
                                                        color={getTransactionStatus(item.transaction_type).textColor}
                                                    />
                                                </div>

                                                <div className="finance-table__cell" data-label="Город">
                                                    {item.city}
                                                </div>

                                                <div className="finance-table__cell" data-label="Описание">
                                                    {item.description}
                                                </div>
                                            </>
                                        )}

                                        <div className="finance-table__cell finance-table__cell--date" data-label="Дата">
                                            <Tag
                                                name={item.date ? getTimeDate(item.date, "date") : ""}
                                                color="#277DFF"
                                                size={"lg"}
                                            />
                                        </div>

                                        <div className="finance-table__cell finance-table__cell--actions">
                                            <RectButton type="button" name="Edit" onClick={() => toggleAccordion(item.id)} />
                                            <RectButton
                                                type="button"
                                                name="Delete"
                                                className="finance-table__delete"
                                                onClick={() => onDelete(item.id)}
                                            />
                                        </div>
                                    </div>

                                    <div
                                        className={`finance-table__accordion ${
                                            openedId === item.id ? "finance-table__accordion--open" : ""
                                        }`}
                                    >
                                        <div className="finance-table__accordion-inner">
                                            <FinanceForm
                                                submitLabel="Update"
                                                initialValues={item}
                                                onSubmit={data => onUpdate(item.id, data)}
                                                onCancel={() => setOpenedId(null)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="finance-table__empty">No records</div>
                    )}
                </div>
            </div>

            <div className="finance__pagination">
                <Pagination
                    totalItems={totalCount}
                    itemsPerPage={ITEMS_PER_PAGE}
                    currentPage={currentPage}
                    onPageChange={onChangePagination}
                    pageUrl={pageUrl}
                />
            </div>
        </div>
    );
};

export default FinanceList;
